import { useEffect, useState, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation, Trans } from 'react-i18next';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import tw, { TwStyle } from 'twin.macro';

import { Button } from '@components/Button';
import { useUser } from '@auth_module/hooks/useUser';
import { SidebarToggler } from '@account_module/components/onboarding/MegaphoneSidebarToggler';
import { useDebouncedState } from '@app/hooks/useDebounce';
import { ONBOARDING_FORM_KEYS } from '@account_module/constants/onBoardingKeys';
import { VerifyCredentialsFormType } from '@account_module/types/Onboarding';
import { useMegaphoneOnboardContext } from '@account_module/context/MegaphoneOnboardContext';
import { useIntegration } from '@account_module/hooks/useIntegrations';
import { IntegrationResponseType } from '@account_module/types/Integration';
import {
  createIntegrationProgress,
  updateIntegration as updateIntegrationServiceFn,
} from '@account_module/services/integrations';

import { verifyMegaphoneCredentials } from '@account_module/services/megaphone';
import { CheckMarkIcon } from '@icons/CheckMarkIcon';
import { Loader } from '@components/Loader';
import loaderImage from '@assets/images/loader.png';

const Form = tw.form`w-[378px] min-w-[300px] absolute top-[50%] left-[50%] translate-y-[-50%] translate-x-[-50%]`;
const Title = tw.h3`text-left text-[24px] leading-[140%] text-primary mb-[4px]`;
const SubTitle = tw.p`text-left text-base text-text-muted-2 mb-[32px] font-normal leading-[150%]`;
const FormField = tw.div`flex items-center gap-[8px] mb-[16px]`;
const InputWrapper = tw.div`w-full relative`;
const Label = tw.label`block text-[14px] font-medium text-primary mb-[4px]`;
const InviteUser = tw.p`text-left text-text-muted-2 text-sm leading-[150%] mt-[16px] [&>a]:text-accent cursor-pointer`;
const ErrorMessage = tw.p`text-left text-error text-sm leading-[150%] mt-[16px]`;
const inputStyle =
  tw`w-full px-[12px] py-[10px] border border-solid border-[#E0E0E0] rounded shadow text-[14px]` as TwStyle;
const SubmitButton = tw(
  Button.Primary
)`bg-[#2A3FCB] disabled:opacity-50 transition-all duration-150 w-full block`;

const InputSpinner = () => (
  <div tw='absolute right-[10px] top-1/2 -translate-y-1/2'>
    <img tw='animate-spin' width='24' height='24' src={loaderImage} alt='loader' />
  </div>
);

export const VerifyCredentialsForm = ({ onSuccess }: { onSuccess: () => void }) => {
  const { t } = useTranslation();
  const { user } = useUser();
  const { setIsInviteModalVisible, setToken, setOrganizationId, organizationId, token } =
    useMegaphoneOnboardContext();
  const [isVerificationLoading, setIsVerificationLoading] = useState<boolean>(false);
  const [isApiValid, setIsApiValid] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const credentialsFormShema = yup.object().shape({
    api_token: yup.string().required('Api key is required').min(5),
    organization_id: yup.string().required('Organization ID is required').min(5),
  });
  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { isValid: isFormValid },
  } = useForm<VerifyCredentialsFormType>({
    resolver: yupResolver(credentialsFormShema),
  });
  const api_field = watch(ONBOARDING_FORM_KEYS.API_TOKEN);
  const organization_field = watch(ONBOARDING_FORM_KEYS.ORG_ID);
  const debouncedApiToken = useDebouncedState(api_field, 500);
  const debouncedOrganizationId = useDebouncedState(organization_field, 500);

  const { integrationId, setIntegrationId } = useMegaphoneOnboardContext();
  const { updateIntegration, isUpdateLoading } = useIntegration(integrationId);

  const verifyCredentials = async () => {
    // Reset
    setIsVerificationLoading(true);
    setErrorMessage(null);
    setIsApiValid(false);

    const { isVerified, error } = await verifyMegaphoneCredentials({
      organization_id: debouncedOrganizationId,
      token: debouncedApiToken,
    });

    if (isVerified) {
      setIsApiValid(true);
      setIsVerificationLoading(false);
    }

    if (error) {
      setErrorMessage(error?.message ?? 'Something went wrong');
      setIsApiValid(false);
      setIsVerificationLoading(false);
    }
  };

  const onSubmit = async (formData: VerifyCredentialsFormType) => {
    if (!isApiValid) return;

    if (integrationId && updateIntegration) {
      if (formData.organization_id !== organizationId && formData.api_token !== token) {
        await updateIntegration({
          integration_organization_id: formData.organization_id,
          secret_token: formData.api_token,
          progress_step: 1,
          include_networks: null,
          exclude_networks: null,
          include_shows: null,
          exclude_shows: null,
        });
      } else {
        await updateIntegration({
          progress_step: 1,
        });
      }
      onSuccess();
    } else {
      await createIntegrationProgress({
        organization_id: formData.organization_id,
        integration_organization_id: formData.organization_id,
        secret_token: formData.api_token,
        token: formData.api_token,
      }).then(async ({ data }: { data: IntegrationResponseType }) => {
        data && setIntegrationId(data?.id);
        setToken(data.secret_token);
        setOrganizationId(data.integration_organization_id);
        await updateIntegrationServiceFn(data?.id, { progress_step: 1 }).then(() => onSuccess());
      });
    }
  };

  useEffect(() => {
    token && setValue(ONBOARDING_FORM_KEYS.API_TOKEN, token);
    organizationId && setValue(ONBOARDING_FORM_KEYS.ORG_ID, organizationId);
  }, [organizationId, token]);

  useEffect(() => {
    if (debouncedApiToken && debouncedOrganizationId) {
      setIsApiValid(false);
      setErrorMessage(null);
      verifyCredentials();
    }
  }, [debouncedApiToken, debouncedOrganizationId]);

  const submitButtonCopy = useMemo(
    (): string =>
      isVerificationLoading && isFormValid
        ? t('account_module.onboard_page.credentials_form.verifying')
        : t('account_module.onboard_page.credentials_form.submit'),
    [isFormValid, isVerificationLoading]
  );

  return (
    <Form onSubmit={handleSubmit(onSubmit)}>
      <Title>
        {t('account_module.onboard_page.credentials_form.title', { name: user?.first_name })}
      </Title>
      <SubTitle>{t('account_module.onboard_page.credentials_form.subtitle')}</SubTitle>
      <Label htmlFor='api_token'>
        {t('account_module.onboard_page.credentials_form.api_token_input.label')}
      </Label>
      <FormField>
        <InputWrapper>
          <input
            id='api_token'
            type='text'
            css={[inputStyle, isApiValid && tw`border-success`]}
            defaultValue={token || ''}
            {...register(ONBOARDING_FORM_KEYS.API_TOKEN)}
          />
          {isApiValid && !!debouncedApiToken.length && !!debouncedOrganizationId.length && (
            <CheckMarkIcon tw='stroke-success w-[20px] h-[20px] absolute right-[10px] top-1/2 -translate-y-1/2' />
          )}
          {isVerificationLoading && <InputSpinner />}
        </InputWrapper>
        <SidebarToggler dataKey={ONBOARDING_FORM_KEYS.API_TOKEN} />
      </FormField>
      <Label htmlFor='organization_id'>
        {t('account_module.onboard_page.credentials_form.org_id_input.label')}
      </Label>
      <FormField>
        <InputWrapper>
          <input
            id='organization_id'
            type='text'
            css={[inputStyle, isApiValid && tw`border-success`]}
            defaultValue={organizationId || ''}
            {...register(ONBOARDING_FORM_KEYS.ORG_ID)}
          />
          {isApiValid && !!debouncedOrganizationId.length && !!debouncedApiToken.length && (
            <CheckMarkIcon tw='stroke-success w-[20px] h-[20px] absolute right-[10px] top-1/2 -translate-y-1/2' />
          )}
          {isVerificationLoading && <InputSpinner />}
        </InputWrapper>
        <SidebarToggler dataKey={ONBOARDING_FORM_KEYS.ORG_ID} />
      </FormField>
      <SubmitButton type='submit' disabled={!isApiValid || isUpdateLoading}>
        {isUpdateLoading ? <Loader size='sm' theme='dark' /> : submitButtonCopy}
      </SubmitButton>
      {errorMessage && <ErrorMessage>{errorMessage}</ErrorMessage>}
      <InviteUser onClick={() => setIsInviteModalVisible(true)}>
        <Trans i18nKey='account_module.onboard_page.credentials_form.invite_coworker_link'>
          <span tw='text-accent underline'>Invite a team member</span> to help you with onboarding.
        </Trans>
      </InviteUser>
    </Form>
  );
};
